import { SyncTable } from "../store/slices/syncSlice";
import {
  upsertRecipesFromSync,
  removeRecipesFromSync,
} from "../store/slices/recipesSlice";
import {
  upsertIngredientsFromSync,
  removeIngredientsFromSync,
} from "../store/slices/ingredientsSlice";
import {
  upsertMethodStepsFromSync,
  removeMethodStepsFromSync,
} from "../store/slices/methodStepsSlice";
import {
  upsertRecipeCollectionsFromSync,
  removeRecipeCollectionsFromSync,
} from "../store/slices/recipeCollections";
import {
  upsertCollectionsFromSync,
  removeCollectionsFromSync,
} from "../store/slices/collectionsSlice";
import { upsertRecentViewsFromSync } from "../store/slices/recentViewsSlice";
import { upsertCookingProgressFromSync } from "../store/slices/cookingProgressSlice";
import { upsertCookingHistoryFromSync } from "../store/slices/cookingHistorySlice";
import { upsertSearchHistoryFromSync } from "../store/slices/searchHistorySlice";
import {
  upsertMealPlanFromSync,
  removeMealPlanFromSync,
} from "../store/slices/mealPlanSlice";
import {
  upsertShoppingListFromSync,
  removeShoppingListFromSync,
} from "../store/slices/shoppingListSlice";
import {
  upsertShoppingListsFromSync,
  removeShoppingListsFromSync,
} from "../store/slices/shoppingListsSlice";

// Sync rows are heterogeneous across tables, so the engine treats them as loose
// records at this boundary.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Row = Record<string, any>;

const toSnake = (s: string) => s.replace(/[A-Z]/g, (c) => "_" + c.toLowerCase());
const toCamel = (s: string) => s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

// camelCase field -> DB column, where the generic converter is wrong.
const COLUMN_OVERRIDES: Partial<Record<SyncTable, Record<string, string>>> = {
  ingredients: { group: "is_group" }, // `group` is a reserved word in SQL
  methodSteps: { group: "is_group" }, // section headings; `group` reserved in SQL
};

// App-only fields that aren't DB columns and must not be pushed.
const COLUMN_OMIT: Partial<Record<SyncTable, string[]>> = {
  // Device-local image fields — not DB columns: localImageUri = device file path;
  // imagePendingUpload = local sync flag; cachedImageUri = local image cache.
  recipes: ["localImageUri", "imagePendingUpload", "cachedImageUri"],
  collections: ["localImageUri", "imagePendingUpload", "cachedImageUri"],
};

// Redux table key -> DB table name.
export function dbName(table: SyncTable): string {
  return toSnake(table);
}

// Entity (camelCase) -> DB row (snake_case), for push.
export function toRow(table: SyncTable, entity: Row): Row {
  const overrides = COLUMN_OVERRIDES[table] ?? {};
  const omit = COLUMN_OMIT[table] ?? [];
  const row: Row = {};
  for (const [key, value] of Object.entries(entity)) {
    if (omit.includes(key)) continue;
    row[overrides[key] ?? toSnake(key)] = value;
  }
  // Resurrected rows have deletedAt === undefined; send NULL explicitly so the
  // server actually clears the tombstone instead of keeping the old value.
  if (row.deleted_at === undefined) row.deleted_at = null;
  return row;
}

// DB row (snake_case) -> entity (camelCase), for pull.
export function fromRow(table: SyncTable, row: Row): Row {
  const overrides = COLUMN_OVERRIDES[table] ?? {};
  const reverse: Record<string, string> = {};
  for (const [field, col] of Object.entries(overrides)) reverse[col] = field;

  const entity: Row = {};
  for (const [col, value] of Object.entries(row)) {
    const key = reverse[col] ?? toCamel(col);
    entity[key] = value === null ? undefined : value; // null -> undefined (optional fields)
  }
  return entity;
}

// Per-table action that applies pulled rows into Redux (tagged meta.fromSync).
// Row shapes differ per table, so the action map is intentionally loosely typed.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const upsertActions: Record<SyncTable, (rows: any[]) => any> = {
  recipes: upsertRecipesFromSync,
  ingredients: upsertIngredientsFromSync,
  methodSteps: upsertMethodStepsFromSync,
  recipeCollections: upsertRecipeCollectionsFromSync,
  collections: upsertCollectionsFromSync,
  recentViews: upsertRecentViewsFromSync,
  cookingProgress: upsertCookingProgressFromSync,
  cookingHistory: upsertCookingHistoryFromSync,
  searchHistory: upsertSearchHistoryFromSync,
  mealPlan: upsertMealPlanFromSync,
  shoppingLists: upsertShoppingListsFromSync,
  shoppingListItems: upsertShoppingListFromSync,
};

// Per-table action that hard-removes rows locally (tagged meta.fromSync). Only
// the tables that create tombstones (soft-delete flows) are listed; the tombstone
// purge iterates these to drop long-dead rows. Children before parents so a
// removed row never briefly dangles under a still-present FK during the purge.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const removeActions: Partial<Record<SyncTable, (ids: string[]) => any>> = {
  recipeCollections: removeRecipeCollectionsFromSync,
  ingredients: removeIngredientsFromSync,
  methodSteps: removeMethodStepsFromSync,
  recipes: removeRecipesFromSync,
  collections: removeCollectionsFromSync,
  mealPlan: removeMealPlanFromSync,
  shoppingLists: removeShoppingListsFromSync,
  shoppingListItems: removeShoppingListFromSync,
};

// Tables the tombstone purge scans, ordered children-first.
export const PURGE_TABLES = Object.keys(removeActions) as SyncTable[];

// Tables worth a low-latency Realtime subscription: the shared recipe content a
// user edits across devices. High-churn / device-local tables (recentViews,
// searchHistory, cooking*) are deliberately left to the fallback poll so a burst
// of view/search writes on one device doesn't storm every other device with
// resyncs. A Realtime event only *nudges* runSync, which pulls every table
// anyway, so this list only decides what we get *notified* about.
export const REALTIME_TABLES: SyncTable[] = [
  "recipes",
  "ingredients",
  "methodSteps",
  "recipeCollections",
  "collections",
  "mealPlan",
  "shoppingLists",
  "shoppingListItems",
];

// FK-safe order: parents before children (matters for push).
export const SYNC_TABLE_ORDER: SyncTable[] = [
  "recipes",
  "collections",
  "ingredients",
  "methodSteps",
  "recipeCollections",
  "cookingHistory",
  "cookingProgress",
  "recentViews",
  "searchHistory",
  "mealPlan", // references recipes → after recipes (which is first)
  "shoppingLists", // parent of shoppingListItems → before it
  "shoppingListItems", // references shoppingLists via list_id → after it
];
